import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import {
  AccessTime,
  ArrowBack,
  CalendarToday,
  CheckCircle,
  Refresh,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";

const PRIMARY = "#4569ea";
const SUCCESS = "#22c55e";
const ERROR = "#ef4444";
const WARNING = "#f59e0b";

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "all", label: "All" },
];

const pad = (n) => String(n).padStart(2, "0");

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getPeriodRange = (period) => {
  if (period === "all") return {};

  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);

  if (period === "week") {
    const day = start.getDay();
    start.setDate(start.getDate() - day);
  }

  if (period === "month") {
    start.setDate(1);
  }

  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
};

const formatHours = (row) => {
  const cleanHoursLabel = (value) => {
    if (!value || value === "00:00") return "";
    const match = String(value).match(/^(\d+):(\d+(?:\.\d+)?)/);
    if (!match) return "";
    return `${pad(Number(match[1]))}:${pad(Math.floor(Number(match[2])))}`;
  };

  const savedLabel = cleanHoursLabel(row?.workHoursFormatted);
  if (savedLabel) return savedLabel;

  if (Number.isFinite(Number(row?.workHours)) && Number(row.workHours) > 0) {
    const totalMinutes = Math.round(Number(row.workHours) * 60);
    return `${pad(Math.floor(totalMinutes / 60))}:${pad(totalMinutes % 60)}`;
  }

  if (row?.punchIn?.time && row?.punchOut?.time) {
    const diffMs = new Date(row.punchOut.time).getTime() - new Date(row.punchIn.time).getTime();
    if (Number.isFinite(diffMs) && diffMs > 0) {
      const totalMinutes = Math.round(diffMs / 60000);
      return `${pad(Math.floor(totalMinutes / 60))}:${pad(totalMinutes % 60)}`;
    }
  }

  return "-";
};

const getRowKm = (row) => Number(row?.km ?? row?.distanceKm ?? row?.totalKm ?? 0) || 0;
const getRowVisits = (row) => Number(row?.visits ?? row?.visitCount ?? row?.totalVisits ?? 0) || 0;
const getRowExpense = (row) => Number(row?.expenseAmount ?? row?.expense ?? row?.totalExpense ?? 0) || 0;
const getDateKey = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const statusColor = (status) => {
  if (status === "present") return { bg: alpha(SUCCESS, 0.12), color: SUCCESS };
  if (status === "holiday" || status === "leave") return { bg: alpha(WARNING, 0.12), color: WARNING };
  return { bg: alpha(ERROR, 0.1), color: ERROR };
};

export default function AttendanceHistory() {
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, fetchAPI } = useAuth();
  const [period, setPeriod] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const targetUserId = userId || user?._id;
  const memberInfo = location.state || {};
  const memberName = memberInfo.memberName || user?.name || user?.firstName || "Selected User";
  const memberRole = memberInfo.memberRole || user?.role || "";

  const loadHistory = useCallback(async () => {
    if (!targetUserId) return;
    setLoading(true);
    setError("");

    try {
      const query = new URLSearchParams({
        userId: targetUserId,
        limit: "100",
        ...getPeriodRange(period),
      });
      const res = await fetchAPI(`/attendance?${query.toString()}`);
      const list = res?.data?.attendances || res?.result?.attendances || res?.data || [];
      const attendanceRows = (Array.isArray(list) ? list : [])
          .filter((row) => row?.punchIn?.time || row?.punchOut?.time)
          .sort((a, b) => new Date(b?.date || b?.punchIn?.time || 0) - new Date(a?.date || a?.punchIn?.time || 0));

      setRows(attendanceRows);

      const rowsWithRoadKm = await Promise.all(
        attendanceRows.map(async (row) => {
          const date = getDateKey(row?.date || row?.punchIn?.time);
          if (!date) return row;

          const params = new URLSearchParams({ salesmanId: targetUserId, date }).toString();
          const hasPunchOut = Boolean(row?.punchOut?.time);

          try {
            const distanceRes = await fetchAPI(
              `${hasPunchOut ? "/location/mapbox-distance" : "/location/travel-distance"}?${params}`
            );
            const data = distanceRes?.data || distanceRes?.result || distanceRes;
            const roadKm = Number(data?.totalKm || 0);
            if (Number.isFinite(roadKm) && roadKm > 0) {
              return {
                ...row,
                km: roadKm,
                kmSource: hasPunchOut ? "mapbox" : "road-route",
              };
            }
          } catch {}

          if (hasPunchOut) {
            try {
              const distanceRes = await fetchAPI(`/location/travel-distance?${params}`);
              const data = distanceRes?.data || distanceRes?.result || distanceRes;
              const roadKm = Number(data?.totalKm || 0);
              if (Number.isFinite(roadKm) && roadKm > 0) {
                return {
                  ...row,
                  km: roadKm,
                  kmSource: "road-route",
                };
              }
            } catch {}
          }

          try {
            const gpsRes = await fetchAPI(`/location/distance?${params}`);
            const data = gpsRes?.data || gpsRes?.result || gpsRes;
            const gpsKm = Number(data?.totalKm || 0);
            if (Number.isFinite(gpsKm) && gpsKm > 0) {
              return {
                ...row,
                km: gpsKm,
                kmSource: "gps",
              };
            }
          } catch {}

          return row;
        })
      );

      setRows(rowsWithRoadKm);
    } catch (err) {
      setError(err.message || "Cannot load attendance history");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [fetchAPI, period, targetUserId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const summary = useMemo(() => {
    const present = rows.filter((row) => String(row?.status || "present").toLowerCase() === "present").length;
    const completed = rows.filter((row) => row?.punchIn?.time && row?.punchOut?.time).length;
    const km = rows.reduce((total, row) => total + getRowKm(row), 0);
    const expense = rows.reduce((total, row) => total + getRowExpense(row), 0);
    return { total: rows.length, present, completed, km, expense };
  }, [rows]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1360, mx: "auto" }}>
      <Paper elevation={0} sx={{ overflow: "hidden", borderRadius: 4, border: "1px solid #e2e8f0" }}>
        <Box sx={{ px: { xs: 2, md: 3 }, py: 2.5, background: "linear-gradient(135deg, #172554, #3b82f6)", color: "#fff" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate(-1)}
                sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.35)", fontWeight: 800 }}
              >
                Back
              </Button>
              <Box>
                <Typography variant="h5" fontWeight={900}>Attendance History</Typography>
                <Typography sx={{ opacity: 0.88, fontWeight: 700 }}>{memberName}{memberRole ? ` - ${memberRole}` : ""}</Typography>
              </Box>
            </Stack>
            <Chip label={period === "all" ? "All Time" : PERIODS.find((p) => p.key === period)?.label} sx={{ bgcolor: "rgba(255,255,255,0.16)", color: "#fff", fontWeight: 800 }} />
          </Stack>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {PERIODS.map((item) => (
              <Button
                key={item.key}
                variant={period === item.key ? "contained" : "outlined"}
                startIcon={<CalendarToday />}
                onClick={() => setPeriod(item.key)}
                size="small"
                sx={{ fontWeight: 800, borderRadius: 6 }}
              >
                {item.label}
              </Button>
            ))}
            <Button variant="outlined" startIcon={<Refresh />} onClick={loadHistory} size="small" sx={{ fontWeight: 800, borderRadius: 6 }}>
              Refresh
            </Button>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" }, gap: 1.5, mb: 2 }}>
            {[
              ["Days", summary.total],
              ["Present", summary.present],
              ["Completed", summary.completed],
              ["KM", summary.km.toFixed(2)],
              ["Expense", `Rs. ${summary.expense.toFixed(0)}`],
            ].map(([label, value]) => (
              <Box key={label} sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 2, bgcolor: "#fff" }}>
                <Typography sx={{ color: "#64748b", fontSize: "0.75rem", fontWeight: 900, textTransform: "uppercase" }}>{label}</Typography>
                <Typography sx={{ fontSize: "1.35rem", fontWeight: 900, color: "#0f172a" }}>{value}</Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 2, overflow: "hidden", bgcolor: "#fff" }}>
            <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid #e2e8f0" }}>
              <Typography sx={{ fontWeight: 900 }}>Daily Summary</Typography>
              <Typography sx={{ color: "#64748b", fontSize: "0.85rem" }}>Real punch in, punch out, hours, status, km, visits, and expense records</Typography>
            </Box>
            <Box sx={{ overflowX: "auto" }}>
              <Box sx={{ minWidth: 1060 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "150px 130px 190px 110px 130px 90px 90px 120px", gap: 1, px: 2.5, py: 1.5, bgcolor: "#f8fafc", fontWeight: 900 }}>
                  <Box>Date</Box>
                  <Box>Punch In</Box>
                  <Box>Punch Out</Box>
                  <Box>Hours</Box>
                  <Box>Status</Box>
                  <Box>KM</Box>
                  <Box>Visits</Box>
                  <Box>Expense</Box>
                </Box>
                {loading ? (
                  <Box sx={{ py: 5, textAlign: "center" }}><CircularProgress size={26} /></Box>
                ) : rows.length === 0 ? (
                  <Box sx={{ py: 5, textAlign: "center", color: "#64748b", fontWeight: 700 }}>No attendance history found.</Box>
                ) : rows.map((row) => {
                  const hasPunchOut = !!row?.punchOut?.time;
                  const status = String(row?.status || (row?.punchIn?.time ? "present" : "absent")).toLowerCase();
                  const colors = statusColor(status);
                  const km = getRowKm(row);
                  const visits = getRowVisits(row);
                  const expense = getRowExpense(row);

                  return (
                    <Box key={row?._id || row?.id || row?.date} sx={{ display: "grid", gridTemplateColumns: "150px 130px 190px 110px 130px 90px 90px 120px", gap: 1, px: 2.5, py: 1.8, alignItems: "center", borderTop: "1px solid #f1f5f9" }}>
                      <Box sx={{ fontWeight: 900 }}>{formatDate(row?.date || row?.punchIn?.time)}</Box>
                      <Box>{row?.punchIn?.time ? formatTime(row.punchIn.time) : "-"}</Box>
                      <Box>
                        {hasPunchOut ? formatTime(row.punchOut.time) : (
                          <Box>
                            <Typography component="span" sx={{ color: PRIMARY, fontWeight: 900 }}>Ongoing</Typography>
                            <Typography sx={{ color: "#64748b", fontSize: "0.75rem" }}>No punch out recorded</Typography>
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ color: hasPunchOut ? PRIMARY : "#64748b", fontWeight: 900 }}>
                        <AccessTime sx={{ fontSize: 15, verticalAlign: "text-bottom", mr: 0.5 }} />
                        {formatHours(row)}
                      </Box>
                      <Box>
                        <Chip
                          size="small"
                          icon={status === "present" ? <CheckCircle /> : undefined}
                          label={status}
                          sx={{ textTransform: "capitalize", bgcolor: colors.bg, color: colors.color, fontWeight: 900, "& .MuiChip-icon": { color: colors.color } }}
                        />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 900, lineHeight: 1 }}>{km.toFixed(2)}</Typography>
                        {row?.kmSource && (
                          <Typography sx={{ color: "#94a3b8", fontSize: "0.68rem", mt: 0.25 }}>
                            {row.kmSource === "gps"
                              ? "GPS verified"
                              : row.kmSource === "mapbox"
                                ? "Mapbox route"
                                : "Road route"}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ fontWeight: 800 }}>{visits}</Box>
                      <Box sx={{ fontWeight: 900 }}>Rs. {expense.toFixed(0)}</Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
